/**
 * HandoffDemoScreen — public marketing/demo surface for the new
 * `/handoff/demo` route (issue #256).
 *
 * What this is for:
 *   A no-real-data, no-server-write preview of the kid practice
 *   experience AND a peek at the parent dashboard. A school-based
 *   SLP or district admin can share the URL with their IT/privacy
 *   team and have them verify the experience without any data being
 *   persisted on our side. Per the plan doc, this is "the single
 *   highest-conversion asset for school-based SLPs" — it must
 *   render without any Supabase backend calls.
 *
 * Hard constraints (acceptance criteria #2):
 *   - No server writes (no Supabase calls at all).
 *   - No localStorage persistence beyond a `visited: true` flag.
 *   - No analytics.
 *   - A "Demo mode — no data is saved" banner is pinned at the top
 *     of every screen of the flow (the welcome screen, the
 *     playing screens, and the dashboard peek).
 *
 * Implementation notes:
 *   - The landing (welcome phase) is a marketing showcase added
 *     in issue #435: a `MarketingHero` + 3-step "How it works" +
 *     "What you'll see" preview row + 4-card "Privacy by design"
 *     trust grid + "Trusted by" placeholder + a bottom CTA. The
 *     landing tells the district-IT-reviewer story before asking
 *     them to interact.
 *   - The playing phase is unchanged: the kid experience is the
 *     existing `PracticeActivityRouter` + `PictureNamingPlayer` so
 *     prospects see exactly what their kids will see.
 *   - The parent dashboard peek is a hardcoded rollup. It does NOT
 *     call `api-get-practice-rollup` (that would touch Supabase).
 *   - Trials are scored client-side in component state; the score
 *     is surfaced at the end of the run as the "what the parent
 *     will see" preview.
 *   - A `visited: true` flag is written to localStorage so we can
 *     tell, if a prospect reports an issue, whether they actually
 *     reached the demo (vs. copy-pasted the URL wrong). This is
 *     the ONLY write — no nickname, no hash, no attempt rows.
 *
 * Out of scope (per the issue body):
 *   - A live marketing landing page (separate ticket).
 *   - A/B testing the demo mode UI for conversion — defer.
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import PracticeActivityRouter from './student/archetypes/PracticeActivityRouter';
import type { PracticeActivityItemLike } from './student/archetypes/shared';
import { MarketingHero } from '../components/MarketingHero';
import { ProcessSteps } from '../components/ProcessSteps';
import type { ProcessStep } from '../components/ProcessSteps';
import { TrustCard } from '../components/TrustCard';
import {
  DashboardPreviewIllustration,
  KidPracticesIllustration,
  LockIconIllustration,
  ParentSeesProgressIllustration,
  PracticePreviewIllustration,
  SlpSendsLinkIllustration,
} from '../components/marketing/illustrations';

/**
 * Paths to the three local SVG stimuli used by the demo's
 * picture-naming trials. These live in /public/demo/ so they're
 * bundled as static assets and served verbatim by Vite — no
 * network fetch, no CDN, no third-party tracker. Served at
 * `/demo/{name}.svg` at runtime.
 *
 * We bundle real stimuli (rather than relying on the
 * PictureNamingPlayer's empty-image fallback) because the
 * `/handoff/demo` surface is the marketing team's primary
 * share-with-district-IT asset — a frame-with-emoji placeholder
 * reads as broken to a non-engineer reviewer. Tiny inline-shaped
 * SVGs (~1 KB each) keep the demo feeling like a real product
 * without adding asset-pipeline complexity.
 */
const DEMO_STIMULI = {
  cat: '/demo/cat.svg',
  sun: '/demo/sun.svg',
  apple: '/demo/apple.svg',
} as const;

/** Single hardcoded picture-naming activity. 3 trials. */
const DEMO_TRIALS: ReadonlyArray<{
  image_url: string;
  expected: string;
  choices: string[];
}> = [
  {
    image_url: DEMO_STIMULI.cat,
    expected: 'cat',
    choices: ['cat', 'dog', 'pig', 'cow'],
  },
  {
    image_url: DEMO_STIMULI.sun,
    expected: 'sun',
    choices: ['sun', 'moon', 'star', 'cloud'],
  },
  {
    image_url: DEMO_STIMULI.apple,
    expected: 'apple',
    choices: ['apple', 'orange', 'ball', 'book'],
  },
];

const DEMO_ITEM: PracticeActivityItemLike = {
  id: 'demo-item-1',
  target_trials: DEMO_TRIALS.length,
  activity_payload: { trials: DEMO_TRIALS },
  scoring_rubric: { archetype: 'picture_naming' },
};

/**
 * Hardcoded sample rollup — what a parent would see on their
 * dashboard after the kid has practiced for a couple of weeks.
 * Kept fully static so the demo doesn't fetch anything from the
 * rollup endpoint.
 */
const DEMO_ROLLUP = {
  studentFirstName: 'Otter',
  weeklyComparison: {
    thisWeekTrials: 24,
    lastWeekTrials: 18,
    thisWeekAccuracy: 79,
    lastWeekAccuracy: 71,
  },
  goals: [
    {
      title: 'Name common nouns (/k/, /g/)',
      sparkline: [10, 12, 14, 16, 18, 22],
      lastPracticed: 'Yesterday',
      accuracyThisWeek: 82,
    },
    {
      title: 'Follow 2-step directions',
      sparkline: [8, 10, 11, 13, 14, 15],
      lastPracticed: '2 days ago',
      accuracyThisWeek: 75,
    },
  ],
  sxpLastMessagePreview: "Hi! Otter did great with /k/ this week — let's keep it up over the weekend.",
  sxpLastMessageWhen: 'Yesterday',
};

type Phase = 'welcome' | 'playing' | 'demo-done';

/**
 * Privacy-by-design claims rendered as 4 trust cards on the demo
 * landing (issue #435 AC #4). Each claim maps to an existing
 * implementation contract:
 *   - "No PHI to AI"         → proxy/src/pii.ts strips identifiers.
 *   - "No student names"     → kid player uses nicknames +
 *                                hashed child_identifier.
 *   - "90-day retention"     → cron-purge-expired-parent-threads
 *                                (issue #341).
 *   - "School owns the data" → district export endpoint exists.
 *
 * Marketing copy must stay in sync with reality. If any of these
 * behaviors change, update this array — and the corresponding
 * implementation — in the same PR.
 */
const PRIVACY_CLAIMS: ReadonlyArray<{
  title: string;
  body: string;
}> = [
  {
    title: 'No PHI to AI',
    body: 'Student names and identifiers are stripped before any AI call. De-identified goal text + age range only.',
  },
  {
    title: 'No student names stored',
    body: 'Kids log in with a nickname + class code. Real names never leave the device.',
  },
  {
    title: '90-day message retention',
    body: 'Parent-SLP messages auto-purge after 90 days. School-district controlled.',
  },
  {
    title: 'School owns the data',
    body: 'Districts export all data anytime. We don’t sell, share, or train on student data.',
  },
];

/**
 * "How it works" 3-step narrative for the demo landing (AC #2).
 * Each step pairs an illustration (from `./marketing/illustrations`)
 * with a short body line.
 */
const PROCESS_STEPS: ReadonlyArray<ProcessStep> = [
  {
    number: 1,
    title: 'SLP sends a link',
    body: 'Your SLP emails or texts a one-tap practice link to each family. No app install, no account creation.',
    illustration: <SlpSendsLinkIllustration />,
  },
  {
    number: 2,
    title: 'Kid practices independently',
    body: 'Kid-friendly activities — picture naming, sentence completion, following directions — at the kid’s own pace.',
    illustration: <KidPracticesIllustration />,
  },
  {
    number: 3,
    title: 'Parent sees progress',
    body: 'The parent dashboard shows weekly trends, per-goal accuracy, and SLP notes — all in plain language.',
    illustration: <ParentSeesProgressIllustration />,
  },
];

/**
 * Preview thumbnails for the "What you'll see in this demo" 3-card
 * row (AC #3). Each caption makes clear the thumbnail is an
 * illustration, NOT a real screenshot, so prospects don't arrive
 * with wrong expectations.
 */
const PREVIEW_CARDS: ReadonlyArray<{
  title: string;
  body: string;
  caption: string;
  thumb: ReactElement;
}> = [
  {
    title: 'One picture-naming activity with 3 trials',
    body: 'Tap the matching word for each picture. The trial player is the same renderer real practice links use.',
    caption: 'Preview · Illustration',
    thumb: <PracticePreviewIllustration />,
  },
  {
    title: 'A "what your parent will see" preview',
    body: 'After the run, peek at the parent dashboard mockup — trends, goals, and SLP messages.',
    caption: 'Preview · Mock',
    thumb: <DashboardPreviewIllustration />,
  },
  {
    title: 'Zero data sent to any server',
    body: 'Nothing you tap leaves this tab. No accounts, no analytics, no Supabase writes.',
    caption: 'Privacy · Verified',
    thumb: <LockIconIllustration />,
  },
];

/** Per-trial outcome we render into the parent-peek at the end. */
interface TrialResult {
  expected: string;
  response: string;
  isCorrect: boolean;
}

function DemoBanner(): ReactElement {
  // Pinned banner — uses position: sticky inside the .screen shell
  // so it stays visible while the kid scrolls through trials. The
  // banner sits BEFORE the hero so it's the first thing the eye
  // lands on at every viewport.
  return (
    <div className="demo-banner" role="status" aria-label="Demo mode notice">
      <strong>Demo mode — no data is saved.</strong>{' '}
      This page is a preview for school IT / privacy review. Nothing
      you tap is sent to any server, no account is created, and no
      analytics are recorded.
    </div>
  );
}

function Sparkline({ values }: { values: ReadonlyArray<number> }): ReactElement {
  const max = Math.max(...values, 1);
  const min = Math.min(...values);
  const range = Math.max(max - min, 1);
  const width = 120;
  const height = 36;
  const padding = 3;
  const points = values
    .map((value, index) => {
      const x =
        values.length === 1
          ? width / 2
          : padding + (index / (values.length - 1)) * (width - padding * 2);
      const y =
        height - padding - ((value - min) / range) * (height - padding * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
    >
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        points={points}
      />
    </svg>
  );
}

function ParentDashboardPeek({
  results,
}: {
  results: ReadonlyArray<TrialResult>;
}): ReactElement {
  const totalCorrect = results.filter((r) => r.isCorrect).length;
  const summary = `${totalCorrect} / ${results.length} correct this run`;
  return (
    <section className="card stack-md" aria-label="Parent dashboard preview">
      <header className="screen-header">
        <h2>What your parent sees</h2>
        <p className="tagline">
          A preview of the parent dashboard after a couple of weeks
          of practice. Real accounts show trends, goals, and SLP
          messages — none of which is fetched here.
        </p>
      </header>
      <p>
        <strong>Hi! Otter&rsquo;s SLP dropped a quick note:</strong>{' '}
        &ldquo;{DEMO_ROLLUP.sxpLastMessagePreview}&rdquo;
        {' '}<span className="tagline">— {DEMO_ROLLUP.sxpLastMessageWhen}</span>
      </p>
      <div className="card" aria-label="This week vs last week">
        <p>
          <strong>This week vs last week</strong>
        </p>
        <p className="tagline">
          {DEMO_ROLLUP.weeklyComparison.thisWeekTrials} trials
          ({DEMO_ROLLUP.weeklyComparison.thisWeekAccuracy}% correct)
          {' '}— up from{' '}
          {DEMO_ROLLUP.weeklyComparison.lastWeekTrials}
          {' '}({DEMO_ROLLUP.weeklyComparison.lastWeekAccuracy}%) last week.
        </p>
      </div>
      <div className="card" aria-label="Per-goal trends">
        <p>
          <strong>Per-goal trends</strong>
        </p>
        <ul className="goal-trend-list">
          {DEMO_ROLLUP.goals.map((goal) => (
            <li key={goal.title} className="goal-trend-row">
              <div>
                <p>{goal.title}</p>
                <p className="tagline">
                  {goal.accuracyThisWeek}% this week · last
                  practiced {goal.lastPracticed}
                </p>
              </div>
              <Sparkline values={goal.sparkline} />
            </li>
          ))}
        </ul>
      </div>
      <div className="card" aria-label="Just now">
        <p>
          <strong>Just now</strong>
        </p>
        <p>
          {summary}. In a real run this row would write to
          `practice_attempts` and the parent&rsquo;s rollup endpoint
          would pick it up — but this is the demo, so it lives only
          in this tab&rsquo;s memory.
        </p>
      </div>
    </section>
  );
}

export function HandoffDemoScreen(): ReactElement {
  const [phase, setPhase] = useState<Phase>('welcome');
  const [trialIndex, setTrialIndex] = useState(0);
  const [results, setResults] = useState<TrialResult[]>([]);

  // Single localStorage write: a `visited: true` flag. This is
  // the ONLY persistence the demo does, per AC #2. We use it as a
  // smoke signal ("did the URL reach the SPA at all") and never
  // read it back to gate UI.
  useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem('handoff:demo:visited', 'true');
    } catch {
      // localStorage disabled (private mode quota, etc.) — the
      // demo still works, the flag is just lost.
    }
  }, []);

  const totalTrials = DEMO_TRIALS.length;

  function handleTrialComplete(outcome: {
    isCorrect: boolean;
    expected: unknown;
    response: unknown;
  }): void {
    const nextResults: TrialResult[] = [
      ...results,
      {
        expected: String(outcome.expected ?? ''),
        response: String(outcome.response ?? ''),
        isCorrect: Boolean(outcome.isCorrect),
      },
    ];
    setResults(nextResults);
    if (trialIndex + 1 >= totalTrials) {
      setPhase('demo-done');
    } else {
      setTrialIndex(trialIndex + 1);
    }
  }

  function resetDemo(): void {
    setPhase('welcome');
    setTrialIndex(0);
    setResults([]);
  }

  return (
    <main className="screen handoff">
      <DemoBanner />

      {phase === 'welcome' && (
        <>
          {/* Hero — MarketingHero primitive (issue #434/#435).
              The brand monogram, headline, subhead, and primary CTA
              live here; the same primitive backs /upgrade. */}
          <MarketingHero
            headline="EffectiveSLP practice link — try it yourself"
            subhead="See what your SLP’s kid practice link looks like in 60 seconds. No sign-up, no data sent to any server."
            cta={{
              label: 'Start the demo',
              onClick: () => setPhase('playing'),
            }}
            ctaTestId="handoff-demo-cta-top"
          />

          {/* How it works — 3-step narrative (AC #2).
              ProcessSteps owns the section <header> + <h2>; we pass
              the tagline through `subheading` instead of rendering a
              second <h2> here. Issue #482: two consecutive h2s with
              identical "How it works" text would render on a
              page aimed at district IT reviewers. */}
          <ProcessSteps
            steps={PROCESS_STEPS}
            subheading="Three steps from SLP to kid to parent — no app install, no account creation, no data sent to any third party."
          />

          {/* What you'll see — 3 preview cards (AC #3). */}
          <section
            className="marketing-section"
            aria-label="What you'll see in this demo"
            data-testid="handoff-demo-preview-cards"
          >
            <header>
              <h2>What you’ll see in this demo</h2>
              <p className="tagline">
                The same renderer real practice links use — just
                pointed at hardcoded sample data so nothing is saved.
              </p>
            </header>
            <div className="preview-cards">
              {PREVIEW_CARDS.map((card) => (
                // The outer <section> already carries the unique
                // testid `handoff-demo-preview-cards`. The inner
                // cards intentionally carry NO per-card testid —
                // identical testids across the 3 <article>s would
                // trip `getByTestId` callers with a
                // TestingLibraryElementError, and nothing in the
                // suite asserts on an individual card today.
                <article key={card.title} className="preview-card">
                  <div className="preview-card-thumb" aria-hidden="true">
                    {card.thumb}
                  </div>
                  <p className="preview-card-caption">{card.caption}</p>
                  <h3 className="preview-card-title">{card.title}</h3>
                  <p className="preview-card-body">{card.body}</p>
                </article>
              ))}
            </div>
          </section>

          {/* Privacy by design — 4 trust cards in a 2x2 grid (AC #4). */}
          <section
            className="marketing-section"
            aria-label="Privacy by design"
            data-testid="handoff-demo-privacy-section"
          >
            <header>
              <h2>Privacy by design</h2>
              <p className="tagline">
                Built for district IT and privacy review. Every claim
                here maps to a real behavior in the codebase.
              </p>
            </header>
            <div className="trust-cards-grid">
              {PRIVACY_CLAIMS.map((claim) => (
                <TrustCard
                  key={claim.title}
                  illustration={<LockIconIllustration />}
                  title={claim.title}
                  body={claim.body}
                  testId="handoff-demo-trust-card"
                />
              ))}
            </div>
          </section>

          {/* Trusted by — placeholder row, marked so production
              build-time checks can catch accidental ship-to-prod.
              Contract (issue #480):
              `data-test-placeholder="true"` is the build-time signal
              that this section is a stub and MUST NOT reach the
              production bundle. The marker is consumed by
              `scripts/check-placeholders.sh`, wired as the postbuild
              step of `npm run build` in web/package.json and as a
              dedicated CI step in .github/workflows/ci.yml (which
              fails the build on any hit). See also the companion
              test in `web/src/__tests__/HandoffDemoScreen.test.tsx`
              test (f) which pins the marker on the rendered DOM.
              If you replace this stub with real logos, REMOVE the
              attribute — do not leave it on a real component. */}
          <section
            className="trusted-by"
            aria-label="Trusted by school districts"
            data-test-placeholder="true"
          >
            <header>
              <h2>Trusted by</h2>
            </header>
            <div className="trusted-by-row" aria-hidden="true">
              <div className="trusted-by-logo">District logo</div>
              <div className="trusted-by-logo">District logo</div>
              <div className="trusted-by-logo">District logo</div>
              <div className="trusted-by-logo">District logo</div>
              <div className="trusted-by-logo">District logo</div>
            </div>
            <p className="trusted-by-caption">
              Coming soon — interested districts can contact{' '}
              <a href="mailto:partnerships@effectiveslp.app">
                partnerships@effectiveslp.app
              </a>
              .
            </p>
          </section>

          {/* Bottom CTA — visually identical to the hero CTA so
              prospects never wonder which one to tap (AC #7). */}
          <section
            className="handoff-bottom-cta"
            aria-label="Ready to try the demo"
          >
            <p className="handoff-bottom-cta-text">
              Ready to see it for yourself?
            </p>
            <button
              type="button"
              className="snap-button handoff-bottom-cta-button"
              onClick={() => setPhase('playing')}
              data-testid="handoff-demo-cta-bottom"
            >
              Start the demo
            </button>
          </section>
        </>
      )}

      {phase === 'playing' && (
        <>
          <div className="auth-hero">
            <header className="screen-header">
              <h1>Trial {trialIndex + 1} / {totalTrials}</h1>
              <p className="tagline">
                What&rsquo;s this picture? Tap the right word.
              </p>
            </header>
          </div>
          <PracticeActivityRouter
            item={DEMO_ITEM}
            trialIndex={trialIndex}
            totalTrials={totalTrials}
            onTrialComplete={handleTrialComplete}
            onBackToActivities={() => setPhase('welcome')}
          />
        </>
      )}

      {phase === 'demo-done' && (
        <>
          <div className="auth-hero">
            <header className="screen-header">
              <h1>Nice work!</h1>
              <p className="tagline">
                Here&rsquo;s what the parent dashboard would show.
              </p>
            </header>
          </div>
          <ParentDashboardPeek results={results} />
          <button
            type="button"
            className="snap-button"
            onClick={resetDemo}
          >
            Restart the demo
          </button>
          <p className="tagline">
            That&rsquo;s the demo. For a real practice link, ask your
            SLP to share one from the EffectiveSLP app.
          </p>
        </>
      )}
    </main>
  );
}

export default HandoffDemoScreen;
